import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLanguage } from "../../language";
import "./style.css";

const IconLanguage = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 32 32"
    style={{ fill: "currentColor" }}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M15.982 3C8.719 3 4.05 7.7 4.05 14.932c0 3.662 1.16 6.683 3.357 8.82L5.973 29l6.469-2.533c1.13.275 2.35.427 3.57.427 7.263 0 11.932-4.7 11.932-11.932S23.244 3 15.982 3zm0 22.308c-1.251 0-2.624-1.923-3.387-5.036h6.774c-.763 3.113-2.136 5.036-3.387 5.036zm-3.693-6.653a23.424 23.424 0 0 1-.274-3.723c0-1.343.091-2.594.274-3.723h7.385c.184 1.129.275 2.38.275 3.723s-.091 2.594-.275 3.723H12.29zm3.693-14.038c1.251 0 2.624 1.923 3.387 5.036h-6.774c.762-3.113 2.136-5.036 3.387-5.036zm5.31 6.622h4.516c.336 1.068.519 2.32.519 3.723 0 1.404-.183 2.625-.519 3.723h-4.516c.152-1.16.244-2.41.244-3.723 0-1.312-.061-2.563-.244-3.723zm3.875-1.586h-4.15c-.427-1.892-1.068-3.48-1.861-4.639 2.472.61 4.73 2.075 6.011 4.639zM12.808 5.014c-.793 1.16-1.434 2.747-1.861 4.639h-4.15c1.281-2.564 3.54-4.029 6.011-4.639zM6.156 11.24h4.516a28.605 28.605 0 0 0 0 7.446H6.156a13.066 13.066 0 0 1-.52-3.723c0-1.403.184-2.655.52-3.723zm6.164 13.55-3.998 1.556.855-3.113-.366-.335a8.99 8.99 0 0 1-2.015-2.625h4.15c.428 1.892 1.069 3.48 1.862 4.639-.091-.03-.152-.03-.244-.061l-.244-.061zm6.836.122c.793-1.16 1.434-2.747 1.861-4.639h4.15c-1.281 2.533-3.54 3.998-6.011 4.639z" />
  </svg>
);

const IconChevronDown = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    style={{ fill: "currentColor" }}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M7.988 12.11 1 5.098 2.208 3.89l5.78 5.78 5.78-5.78L15 5.098 7.988 12.11z" />
  </svg>
);

export const LocaleSelector = () => {
  const rootRef = useRef(null);
  const intl = useIntl();
  const { locale: language, setLocale } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleDocumentClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const options = [
    { labelId: "header.locale.french", value: "fr" },
    { labelId: "header.locale.english", value: "en" },
  ];

  const currentOption = options.find((option) => option.value === language) || options[0];
  const activeLabel = language.toUpperCase();
  const currentLanguageLabel = intl.formatMessage({ id: currentOption.labelId });

  return (
    <div className="site-language-selector" ref={rootRef}>
      <button
        type="button"
        className="site-language-selector__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={intl.formatMessage(
          { id: "header.locale.current" },
          { language: currentLanguageLabel }
        )}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="site-language-selector__icon" aria-hidden="true">
          <IconLanguage />
        </span>
        <span className="site-language-selector__value">{activeLabel}</span>
      </button>
      <button
        type="button"
        className="site-language-selector__caretButton"
        aria-label={intl.formatMessage({ id: "header.locale.open" })}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="site-language-selector__caret" aria-hidden="true">
          <IconChevronDown />
        </span>
      </button>
      {isOpen ? (
        <div
          className="site-language-selector__menu"
          role="listbox"
          aria-label={intl.formatMessage({ id: "header.locale.choose" })}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={language === option.value}
              className={`site-language-selector__option${language === option.value ? " is-active" : ""}`}
              onClick={() => {
                setLocale(option.value);
                setIsOpen(false);
              }}
            >
              {intl.formatMessage({ id: option.labelId })}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};